import { jsxs, Fragment, jsx } from "react/jsx-runtime";
import { useRef, useCallback } from "react";
import { IndexCellWrapper } from "../Styles.js";
import { useBulkRowSelection } from "../../../context/useBulkRowSelection.js";
import { named } from "../../../utils/named.js";
import { useColumns } from "../../../context/useColumnsContext.js";
import { useColumnSelectionContext } from "../../../context/useColumnSelectionContext.js";
import { getSelectedCellRange } from "../../../hooks/useSelectCells.js";
import { ColumnFocus } from "./ColumnFocus.js";
import ColumnHeaderContent from "./ColumnHeaderContent.js";
import { ColumnOverlay } from "./ColumnOverlay.js";
const ColumnHeader = ({
  column,
  columnChangeType,
  columnContextMenuSlot,
  onFindAndReplace,
  onSort,
  onSearchValue,
  sortOverride,
  onMouseDown,
  onMouseUp,
  onMouseEnter,
  absoluteColumnIndex,
  pinned,
  isLastColumn,
  isFirstColumn,
  style,
  tableId,
  readOnly,
  useFilterByValue,
  pinningEnabled,
  columnSelectionEnabled,
  showColumnMenu,
  header,
  headerInjections
}) => {
  const cellRef = useRef(null);
  const { columnIndexToValue } = useColumns();
  const {
    isSelectingColumns,
    queuedColumnSelections,
    addQueuedColumnSelections,
    applyQueuedColumnSelections,
    setIsSelectingColumns,
    setUniqueColumnSelections
  } = useColumnSelectionContext();
  const { getIsBulkSelecting } = useBulkRowSelection();
  const isBulkSelecting = getIsBulkSelecting && getIsBulkSelecting();
  const handleSelectColumns = useCallback(
    (queued = false) => {
      const selectedCellRange = getSelectedCellRange({ tableId });
      if (selectedCellRange) {
        const [start, end] = selectedCellRange;
        const startRowIndex = start[0];
        const endRowIndex = end[0];
        const startColumnIndex = Math.min(start[1], end[1]);
        const endColumnIndex = Math.max(start[1], end[1]);
        const selectedColumnNames = [];
        if (startRowIndex === 0 && endRowIndex === 0) {
          for (let i = startColumnIndex; i <= endColumnIndex; i++) {
            const columnValue = columnIndexToValue[i];
            selectedColumnNames.push(columnValue);
          }
          if (queued) {
            addQueuedColumnSelections(selectedColumnNames);
          } else {
            setUniqueColumnSelections(selectedColumnNames);
          }
        } else {
          addQueuedColumnSelections([]);
          setUniqueColumnSelections([]);
        }
      }
    },
    [columnIndexToValue, addQueuedColumnSelections]
  );
  const handleToggleSingleColumnSelection = useCallback(() => {
    if (!(column == null ? void 0 : column.value))
      return;
    addQueuedColumnSelections([column.value]);
  }, [column == null ? void 0 : column.value, addQueuedColumnSelections]);
  const handleMouseDown = useCallback(
    (event) => {
      if (isBulkSelecting || event.button === 2)
        return;
      onMouseDown(cellRef, event.button, event.shiftKey);
      setIsSelectingColumns(true);
      if (event.metaKey && !event.shiftKey) {
        handleToggleSingleColumnSelection();
        return;
      }
      if (event.metaKey && event.shiftKey) {
        handleSelectColumns(true);
        return;
      }
      if (event.shiftKey) {
        handleSelectColumns(false);
        return;
      }
      setUniqueColumnSelections((column == null ? void 0 : column.value) ? [column.value] : []);
    },
    [
      cellRef,
      isBulkSelecting,
      handleSelectColumns,
      setIsSelectingColumns,
      handleToggleSingleColumnSelection,
      addQueuedColumnSelections,
      setUniqueColumnSelections
    ]
  );
  const handleMouseEnter = useCallback(
    (event) => {
      if (isBulkSelecting)
        return;
      onMouseEnter(cellRef);
      const queuedSelection = event == null ? void 0 : event.metaKey;
      if (isSelectingColumns) {
        handleSelectColumns(queuedSelection);
      }
    },
    [cellRef, isBulkSelecting, handleSelectColumns, isSelectingColumns]
  );
  const handleMouseUp = useCallback(() => {
    if (isBulkSelecting)
      return;
    onMouseUp(cellRef);
    setIsSelectingColumns(false);
    applyQueuedColumnSelections();
  }, [
    cellRef,
    isBulkSelecting,
    setIsSelectingColumns,
    applyQueuedColumnSelections,
    queuedColumnSelections
  ]);
  const handleClick = useCallback(() => {
    if (isBulkSelecting)
      return;
    onMouseDown(cellRef, void 0, false);
    onMouseUp(cellRef);
  }, [cellRef]);
  return /* @__PURE__ */ jsxs(
    IndexCellWrapper,
    {
      ref: cellRef,
      "data-cell-wrapper": "IndexCellWrapper",
      style,
      pinned,
      isFirstRow: true,
      isFirstColumn,
      isLastColumn,
      onMouseDown: handleMouseDown,
      onMouseUp: handleMouseUp,
      onMouseEnter: handleMouseEnter,
      children: [
        column && header && columnSelectionEnabled && /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx(ColumnOverlay, { isFirstColumn, column }),
          /* @__PURE__ */ jsx(ColumnFocus, { isFirstColumn, column })
        ] }),
        /* @__PURE__ */ jsx(
          ColumnHeaderContent,
          {
            column,
            columnChangeType,
            pinned,
            onSort,
            onClick: handleClick,
            onSearchValue,
            onFindAndReplace,
            sortOverride,
            absoluteColumnIndex,
            cellRef,
            isLastColumn,
            tableId,
            readOnly,
            useFilterByValue,
            pinningEnabled,
            showColumnMenu,
            columnContextMenuSlot,
            headerInjections
          }
        )
      ]
    }
  );
};
named("ColumnHeader", ColumnHeader);
export {
  ColumnHeader as default
};
